// Proportional scale: uniformly rescale every physical dimension of a
// document (mm quantities) by a factor. Angles, ratios, morph amounts, seeds,
// and the export thickness/tolerance stay untouched.

import type { DocumentModel, Vec2 } from './types'
import { CHANNEL_IDS } from './types'

const r3 = (v: number) => Math.round(v * 1000) / 1000
const sv = (p: Vec2, f: number): Vec2 => ({ x: r3(p.x * f), y: r3(p.y * f) })

// Channels whose values are millimetres; angle (deg) and shape (0..1) are not.
const MM_CHANNELS = new Set(['size', 'spacing'])

export function scaleDocument(d: DocumentModel, f: number) {
  if (!(f > 0) || f === 1) return
  d.canvas.width = r3(d.canvas.width * f)
  d.canvas.height = r3(d.canvas.height * f)

  d.boundary.width = r3(d.boundary.width * f)
  d.boundary.height = r3(d.boundary.height * f)
  d.boundary.radius = r3(d.boundary.radius * f)
  d.boundary.cornerRadius = r3(d.boundary.cornerRadius * f)
  d.boundary.nodes = d.boundary.nodes.map((n) => sv(n, f))

  d.clipMargin = r3(d.clipMargin * f)
  for (const g of d.guides) g.value = r3(g.value * f)
  d.layout.jitter = r3(d.layout.jitter * f)
  d.layout.voronoi.cornerRadius = r3(d.layout.voronoi.cornerRadius * f)
  for (const p of d.layout.paths) p.nodes = p.nodes.map((n) => sv(n, f))

  for (const id of CHANNEL_IDS) {
    const set = d.channels[id]
    const mm = MM_CHANNELS.has(id)
    if (mm) set.base = r3(set.base * f)
    for (const c of set.controllers) {
      c.a = sv(c.a, f)
      c.b = sv(c.b, f)
      c.nodes = c.nodes.map((n) => sv(n, f))
      c.radius = r3(c.radius * f)
      if (mm) c.value = r3(c.value * f)
    }
    if (set.image) {
      const r = set.image.rect
      set.image.rect = { x: r3(r.x * f), y: r3(r.y * f), w: r3(r.w * f), h: r3(r.h * f) }
      if (mm) {
        set.image.valueLo = r3(set.image.valueLo * f)
        set.image.valueHi = r3(set.image.valueHi * f)
      }
    }
  }

  if (d.background) {
    d.background.width = r3(d.background.width * f)
    d.background.x = r3(d.background.x * f)
    d.background.y = r3(d.background.y * f)
  }
}
